import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useModule } from '../contexts/ModuleContext';
import { Plus, Plane, MapPin, Calendar, ChevronRight, Search, Home, Globe } from 'lucide-react';
import { ExportButton } from '../components/ExportButton';
import { formatINR } from '../utils/currency';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_STYLES = {
  ongoing: 'bg-blue-50 text-blue-700 border-blue-200',
  submitted: 'bg-amber-50 text-amber-700 border-amber-200',
  approved: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  settled: 'bg-purple-50 text-purple-700 border-purple-200',
};

const formatCurrency = (amount, currency = 'INR') => {
  if (currency === 'USD') return `$${Number(amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  if (currency === 'EUR') return `€${Number(amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  if (currency === 'GBP') return `£${Number(amount || 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  if (currency === 'OTHERS') return `${Number(amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} (Other)`;
  return formatINR(amount);
};

const TripsPage = () => {
  const { user } = useAuth();
  const { activeModule, tripType } = useModule();
  const navigate = useNavigate();
  const [trips, setTrips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [users, setUsers] = useState([]);

  const role = user?.role;
  const effectiveModule = role === 'accounts' ? 'INTERNATIONAL' : (role === 'dgm' || role === 'senior_approver') ? 'DOMESTIC' : activeModule;
  const effectiveTripType = effectiveModule === 'DOMESTIC' ? 'Domestic' : 'International';
  const effectiveCurrency = effectiveModule === 'DOMESTIC' ? 'INR' : 'USD';

  useEffect(() => {
    fetchTrips();
    if (role !== 'bdm') fetchUsers();
  }, [statusFilter, effectiveModule]);

  const fetchTrips = async () => {
    setLoading(true);
    try {
      const params = { trip_type: effectiveTripType };
      if (statusFilter) params.status = statusFilter;
      const res = await axios.get(`${API}/trips`, { params });
      setTrips(res.data);
    } catch (err) {
      console.error('Error fetching trips:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const res = await axios.get(`${API}/users`);
      setUsers(res.data.filter(u => u.role === 'bdm' && u.status === 'active'));
    } catch (err) {
      console.error('Error fetching users:', err);
    }
  };

  const filteredTrips = trips.filter(t =>
    t.trip_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    t.employee_name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="text-gray-500">Loading trips...</div></div>;
  }

  return (
    <div className="p-6 md:p-8">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
            {effectiveModule === 'DOMESTIC' ? <MapPin className="w-5 h-5 text-indigo-500" /> : <Plane className="w-5 h-5 text-emerald-500" />}
            {role === 'bdm' ? 'My Trips' : `${effectiveTripType} Trips`}
          </h1>
          <p className="text-sm text-gray-500 mt-1">{effectiveTripType} travel trips in {effectiveCurrency}</p>
        </div>
        <div className="flex gap-2">
          {role !== 'accounts' && (
            <button onClick={() => setShowCreateModal(true)} className="btn btn-primary" data-testid="create-trip-btn">
              <Plus className="w-4 h-4" /> New {effectiveTripType} Trip
            </button>
          )}
          {['admin', 'hr', 'accounts'].includes(role) && (
            <ExportButton type="trips" fixedFilters={{ trip_type: effectiveTripType, ...(statusFilter ? { status: statusFilter } : {}) }} showFilterUI label="Export" />
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <div className="relative flex-1 max-w-xs">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input type="text" placeholder="Search trips..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
            className="input-field pl-9" data-testid="search-trips-input" />
        </div>
        <div className="flex gap-2 flex-wrap">
          {['', 'ongoing', 'submitted', 'approved', 'settled'].map(status => (
            <button key={status} onClick={() => setStatusFilter(status)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${
                statusFilter === status
                  ? 'bg-indigo-600 text-white border-indigo-600'
                  : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'
              }`}
              data-testid={`filter-${status || 'all'}`}>
              {status ? status.charAt(0).toUpperCase() + status.slice(1) : 'All'}
            </button>
          ))}
        </div>
      </div>

      {/* Trips Grid */}
      {filteredTrips.length === 0 ? (
        <div className="card text-center py-12">
          <Plane className="w-12 h-12 text-gray-300 mx-auto mb-3" strokeWidth={1.5} />
          <p className="text-gray-500 text-sm">No {effectiveTripType.toLowerCase()} trips found</p>
          <p className="text-gray-400 text-xs mt-1">Create a new trip to get started</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredTrips.map(trip => {
            const cur = trip.currency || effectiveCurrency;
            return (
              <div key={trip.id} className="card cursor-pointer hover:border-indigo-200 hover:ring-2 hover:ring-indigo-50"
                onClick={() => navigate(`/trips/${trip.id}`)} data-testid={`trip-card-${trip.id}`}>
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${trip.trip_type === 'International' ? 'bg-emerald-50' : 'bg-indigo-50'}`}>
                      {trip.trip_type === 'International' ? (
                        <Globe className="w-4 h-4 text-emerald-600" />
                      ) : (
                        <Home className="w-4 h-4 text-indigo-600" />
                      )}
                    </div>
                    <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full border ${STATUS_STYLES[trip.status]}`}>
                      {trip.status.toUpperCase()}
                    </span>
                    <span className="text-[10px] font-medium text-gray-400">{cur}</span>
                  </div>
                  <ChevronRight className="w-4 h-4 text-gray-300" />
                </div>
                <h3 className="text-sm font-semibold text-gray-900 mb-1 truncate" style={{ fontFamily: 'Manrope, sans-serif' }}>{trip.trip_name}</h3>
                <p className="text-xs text-gray-500 mb-3">{trip.employee_name}</p>
                <div className="flex items-center gap-3 text-xs text-gray-400 mb-3">
                  <div className="flex items-center gap-1">
                    <Calendar className="w-3 h-3" />{trip.start_date} — {trip.end_date}
                  </div>
                </div>
                <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                  <div>
                    <div className="text-[10px] text-gray-400 uppercase tracking-wider">Advance</div>
                    <div className="text-sm font-semibold text-gray-900">{formatCurrency(trip.advance_amount, cur)}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-[10px] text-gray-400 uppercase tracking-wider">Expenses</div>
                    <div className="text-sm font-semibold text-gray-900">
                      {formatCurrency(trip.total_expenses, cur)} <span className="text-xs text-gray-400 font-normal">({trip.expense_count})</span>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Create Trip Modal */}
      {showCreateModal && (
        <CreateTripModal user={user} users={users} effectiveTripType={effectiveTripType} effectiveCurrency={effectiveCurrency}
          onClose={() => setShowCreateModal(false)}
          onCreated={() => { setShowCreateModal(false); fetchTrips(); }} />
      )}
    </div>
  );
};

const CreateTripModal = ({ user, users, effectiveTripType, effectiveCurrency, onClose, onCreated }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState({
    trip_name: '',
    trip_type: effectiveTripType,
    currency: effectiveCurrency,
    employee_id: user?.role === 'bdm' ? user.id : '',
    employee_name: user?.role === 'bdm' ? user.name : '',
    advance_amount: '',
    start_date: '',
    end_date: '',
    description: ''
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'employee_id' && user?.role !== 'bdm') {
      const selected = users.find(u => u.id === value);
      setForm(prev => ({ ...prev, employee_id: value, employee_name: selected?.name || '' }));
    } else if (name === 'trip_type') {
      const newCurrency = value === 'Domestic' ? 'INR' : 'USD';
      setForm(prev => ({ ...prev, trip_type: value, currency: newCurrency }));
    } else {
      setForm(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.trip_name || !form.start_date || !form.end_date || !form.employee_id) {
      setError('Please fill all required fields');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/trips`, {
        ...form,
        advance_amount: parseFloat(form.advance_amount) || 0
      });
      onCreated();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create trip');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content p-6" onClick={e => e.stopPropagation()} data-testid="create-trip-modal">
        <h2 className="text-lg font-bold text-gray-900 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
          Create {form.trip_type} Trip
        </h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Trip Name *</label>
            <input name="trip_name" value={form.trip_name} onChange={handleChange}
              className="input-field" placeholder="e.g., Mumbai Client Visit" data-testid="trip-name-input" />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Trip Type *</label>
              <select name="trip_type" value={form.trip_type} onChange={handleChange}
                className="input-field" data-testid="trip-type-select">
                <option value="Domestic">Domestic</option>
                <option value="International">International</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Currency</label>
              {form.trip_type === 'International' ? (
                <select name="currency" value={form.currency} onChange={handleChange}
                  className="input-field" data-testid="trip-currency-select">
                  <option value="USD">USD ($)</option>
                  <option value="EUR">EUR (€)</option>
                  <option value="GBP">GBP (£)</option>
                </select>
              ) : (
                <input value="INR (₹)" disabled className="input-field bg-gray-50" />
              )}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Advance Amount ({form.currency})</label>
            <input name="advance_amount" type="number" value={form.advance_amount} onChange={handleChange}
              className="input-field" placeholder="0" min="0" step="0.01" data-testid="advance-amount-input" />
          </div>

          {user?.role !== 'bdm' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">BDM / Employee *</label>
              <select name="employee_id" value={form.employee_id} onChange={handleChange}
                className="input-field" data-testid="employee-select">
                <option value="">Select BDM</option>
                {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
              </select>
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Start Date *</label>
              <input name="start_date" type="date" value={form.start_date} onChange={handleChange}
                className="input-field" data-testid="start-date-input" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">End Date *</label>
              <input name="end_date" type="date" value={form.end_date} onChange={handleChange}
                className="input-field" data-testid="end-date-input" />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea name="description" value={form.description} onChange={handleChange}
              className="input-field h-20 resize-none" placeholder="Trip purpose or notes" data-testid="trip-description-input" />
          </div>

          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm" data-testid="trip-create-error">{error}</div>
          )}

          <div className="flex gap-3 pt-2">
            <button type="submit" disabled={loading} className="btn btn-primary flex-1 justify-center" data-testid="submit-trip-btn">
              {loading ? 'Creating...' : `Create ${form.trip_type} Trip`}
            </button>
            <button type="button" onClick={onClose} className="btn btn-secondary" data-testid="cancel-trip-btn">Cancel</button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default TripsPage;
